import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

export default function RecordDuesModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [owners, setOwners] = useState([]);
  const [formData, setFormData] = useState({
    owner_id: '',
    unit_id: '',
    amount: 350,
    due_date: '',
    paid_date: '',
    status: 'paid',
    payment_method: 'stripe',
    notes: ''
  });

  useEffect(() => {
    if (open) {
      fetchOwners();
      // Set default due date to today
      const today = new Date().toISOString().split('T')[0];
      setFormData(prev => ({ ...prev, paid_date: today }));
    }
  }, [open]);

  const fetchOwners = async () => {
    try {
      const response = await api.get('/hoa/owners');
      setOwners(response.data.owners || []);
    } catch (error) {
      console.error('Error fetching owners:', error);
    }
  };

  const handleOwnerSelect = (ownerId) => {
    const owner = owners.find(o => o.id === ownerId);
    setFormData({
      ...formData,
      owner_id: ownerId,
      unit_id: owner?.unit_id || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/hoa/dues/record', formData);
      toast.success('HOA dues recorded successfully!');
      onSuccess();
      onClose();
      setFormData({
        owner_id: '',
        unit_id: '',
        amount: 350,
        due_date: '',
        paid_date: '',
        status: 'paid',
        payment_method: 'stripe',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record dues');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record HOA Dues Payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Select Owner</Label>
            <Select value={formData.owner_id} onValueChange={handleOwnerSelect} required>
              <SelectTrigger>
                <SelectValue placeholder="Select owner" />
              </SelectTrigger>
              <SelectContent>
                {owners.map(owner => (
                  <SelectItem key={owner.id} value={owner.id}>
                    {owner.first_name} {owner.last_name} - Unit {owner.unit_number || 'N/A'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input 
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="late">Late</SelectItem>
                  <SelectItem value="waived">Waived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input 
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Paid Date</Label>
              <Input 
                type="date"
                value={formData.paid_date}
                onChange={(e) => setFormData({...formData, paid_date: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select value={formData.payment_method} onValueChange={(val) => setFormData({...formData, payment_method: val})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stripe">Online Payment</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="manual">Manual Entry</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <textarea 
              className="w-full border rounded p-2 min-h-[60px]"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any additional notes..."
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Recording...' : 'Record Payment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
